/**
 * DeleteAccountCard — "Delete my account" for parents, coaches and admins.
 *
 * Nothing is deleted from here. It files a request that the owner reviews;
 * the card then shows the request as pending. Owners never see this card
 * (the API refuses owner self-deletion anyway).
 */

import React, { useEffect, useState } from "react";
import { AlertTriangle, Clock, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import usersService from "../api/services/users.service";
import { formatDate } from "../utils/format";

export default function DeleteAccountCard({ className = "" }) {
  const [pending, setPending] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    usersService
      .getMyDeletionRequest()
      .then((data) => !cancelled && setPending(data || null))
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const result = await usersService.requestMyDeletion(reason.trim() || undefined);
      toast.success(result.message, { duration: 7000 });
      setPending({ created_at: result.requested_at, reason: reason.trim() || null });
      setOpen(false);
      setReason("");
    } catch (error) {
      toast.error(error.message || "Could not send the request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-3xl shadow-sm border border-[#DF1C41]/20 p-6 font-manrope ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
        <div className="min-w-0">
          <h2 className="text-fluid-md font-semibold text-[#DF1C41]">Delete my account</h2>
          {pending ? (
            <p className="text-sm text-text-muted leading-[150%] mt-1 flex items-start gap-2">
              <Clock className="w-4 h-4 mt-0.5 shrink-0 text-[#f4b728]" />
              <span>
                Deletion requested{pending.created_at ? ` on ${formatDate(pending.created_at)}` : ""}.
                The owner will review it. Your account stays active until it is approved.
              </span>
            </p>
          ) : (
            <p className="text-sm text-text-muted leading-[150%] mt-1">
              This sends a request to the owner. Nothing is deleted until they approve it.
              For parents, approval also cancels your children&apos;s enrollments and stops billing.
            </p>
          )}
        </div>
        {loaded && !pending && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 px-4 h-10 rounded-full text-sm font-bold text-[#DF1C41] border border-[#DF1C41]/40 hover:bg-[#DF1C41]/5 transition"
          >
            Request deletion
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-[12px] shadow-2xl max-w-md w-full border border-border-light">
            <div className="flex items-start justify-between p-6 pb-4 border-b border-border-light">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#DF1C41]/10 rounded-full flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-[#DF1C41]" />
                </div>
                <h3 className="text-lg font-bold text-heading-dark">Request account deletion?</h3>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="p-1 hover:bg-gray-100 rounded transition"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-text-muted leading-relaxed">
                The owner will be asked to review this. You can keep using your account until
                they approve it.
              </p>
              <label className="block text-sm font-medium text-text-muted">
                Reason (optional)
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  disabled={submitting}
                  placeholder="Let the owner know why"
                  className="mt-2 w-full border border-border-light rounded-xl px-4 py-3 text-nuetral-200 font-medium outline-none focus:ring-2 focus:ring-[#f4b728] disabled:opacity-50"
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-neutral-lightest rounded-b-[12px] border-t border-border-light">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-text-primary border border-border-light hover:bg-white rounded-[8px] transition disabled:opacity-50"
              >
                Keep my account
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#DF1C41] hover:bg-[#b8163a] rounded-[8px] transition disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Send request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
